import { ECategoryType, EFeatureFamilyType, ECategoryErrorType, EDropCategory } from '../../data/Constants';

import type { ISection } from '../../../types/ITypes';
import { findParent } from './CategoryTreeHelper';
import { showCategoryError } from './CategoryErrorHelper';

/**
 * Checks if the target drop zone is a multi-numeric category
 * @param {any} over - The drag and drop 'over' object containing target information
 * @returns {boolean} True if the target is MultiNumeric or Numeric type
 */
export const isTargetMultiNumeric = (over: any): boolean => {
  const overItemType = over.data?.current?.itemType;
  if (overItemType === ECategoryType.MultiNumeric || overItemType === EFeatureFamilyType.Numeric) {
    return true;
  }

  return false;
};

/**
 * Determines which zone/container the item is being dragged to
 * @param {any} over - The drag and drop 'over' object containing target zone information
 * @returns {string} The target zone identifier (Assigned, Unassigned, or category ID)
 */
export const determineTargetZone = (over: any): string => {
  if (over.data?.current?.container === EDropCategory.Assigned) {
    return EDropCategory.Assigned;
  } else if (over.data?.current?.type === EDropCategory.Category) {
    return over.id as string;
  } else if (over.id === EDropCategory.Assigned || over.id === EDropCategory.Unassigned) {
    return over.id as string;
  } else {
    return over.data?.current?.container ?? (over.id as string);
  }
};

/**
 * Checks if the drag operation is attempting to drop a MultiNumeric category under another MultiNumeric category
 * @param {ISection} draggedNode - The section being dragged
 * @param {ISection} targetNode - The target section
 * @returns {boolean} True if both nodes are MultiNumeric type
 */
function isMultiNumericDrop(draggedNode: ISection, targetNode: ISection): boolean {
  return (
    draggedNode.type === ECategoryType.MultiNumeric &&
    targetNode.type === ECategoryType.MultiNumeric
  );
}

/**
 * Checks if a category is being dropped onto itself (same description validation)
 * @param {ISection} draggedNode - The section being dragged
 * @param {ISection} targetNode - The target section
 * @returns {boolean} True if both nodes have the same description
 */
function isSelfDrop(draggedNode: ISection, targetNode: ISection): boolean {
  return draggedNode.description === targetNode.description;
}

/**
 * Checks if any category is being dropped under a MultiNumeric category
 * @param {ISection} targetNode - The target section
 * @returns {boolean} True if the target is a MultiNumeric category
 */
function isDropUnderMultiNumeric(targetNode: ISection): boolean {
  return targetNode.type === ECategoryType.MultiNumeric;
}

/**
 * Checks if a top-level non-numeric category already has a non-numeric subcategory
 * @param {ISection[]} clonedSections - The sections array
 * @param {ISection} draggedNode - The section being dragged
 * @returns {boolean} True if it's a top-level non-numeric category with a non-numeric subcategory
 */
function isTopLevelNonNumericWithSubcategory(
  clonedSections: ISection[],
  draggedNode: ISection
): boolean {
  const isTopLevelNonNumeric =
    draggedNode.type !== ECategoryType.MultiNumeric &&
    !findParent(clonedSections, draggedNode.code);

  const hasNonNumericSubcategory =
    isTopLevelNonNumeric &&
    Array.isArray(draggedNode.sections) &&
    draggedNode.sections.some((sub) => sub.type !== ECategoryType.MultiNumeric);

  return isTopLevelNonNumeric && hasNonNumericSubcategory;
}

/**
 * Validates that non-numeric categories can only be dropped to non-numeric top-level categories
 * @param {ISection[]} clonedSections - The sections array
 * @param {ISection} draggedNode - The section being dragged
 * @param {ISection} targetNode - The target section
 * @returns {boolean} True if attempting to drop a non-numeric category to a subcategory
 */
function isNonNumericDropToSubcategory(
  clonedSections: ISection[],
  draggedNode: ISection,
  targetNode: ISection
): boolean {
  if (
    draggedNode.type !== ECategoryType.MultiNumeric &&
    targetNode.type !== ECategoryType.MultiNumeric
  ) {
    const parentOfTarget = findParent(clonedSections, targetNode.code);
    return !!parentOfTarget;
  }
  return false;
}

/**
 * Validates if a category drag and drop operation is invalid based on multiple rules
 * @param {ISection[]} clonedSections - The cloned sections array
 * @param {ISection} draggedNode - The section being dragged
 * @param {ISection} targetNode - The target section
 * @param {boolean} showError - Whether to display error dialog
 * @param {any} dispatch - Redux dispatch function
 * @param {any} t - Translation function
 * @returns {boolean} True if the drop operation is invalid
 */
export function isInvalidCategoryDrop(
  clonedSections: ISection[],
  draggedNode: ISection,
  targetNode: ISection,
  showError: boolean,
  dispatch: any,
  t: any
): boolean {
  const validationChecks = [
    () => isMultiNumericDrop(draggedNode, targetNode),
    () => isSelfDrop(draggedNode, targetNode),
    () => isDropUnderMultiNumeric(targetNode),
    () => isTopLevelNonNumericWithSubcategory(clonedSections, draggedNode),
    () => isNonNumericDropToSubcategory(clonedSections, draggedNode, targetNode),
  ];

  const isInvalid = validationChecks.some((check) => check());

  if (isInvalid && showError) {
    showCategoryError(dispatch, t, ECategoryErrorType.INVALID_DRAG_DROP);
  }

  return isInvalid;
}

/**
 * Validates if an item can be dropped into a specific category/section
 * For MultiNumeric sections, only Numeric items are allowed
 * For other sections, all item types are allowed
 *
 * @param sectionType - The type of the target section
 * @param itemType - The type of the item being dragged
 * @param isTargetCategory - Optional flag for whether target is a category section
 * @returns Whether the item can be dropped in the target
 */
export const isValidItemDrop = (
  sectionType: string | undefined,
  itemType: string | undefined,
  isTargetCategory: boolean = false
): boolean => {
  // For MultiNumeric sections, only accept Numeric items
  if (sectionType === ECategoryType.MultiNumeric) {
    return itemType === EFeatureFamilyType.Numeric;
  }

  // For category targets that aren't explicitly MultiNumeric, also validate
  if (isTargetCategory && itemType) {
    return itemType === EFeatureFamilyType.Numeric;
  }

  // For non-MultiNumeric sections or no section type, allow any item
  return true;
};
